import React from 'react';
import { ChevronDown } from 'lucide-react';

function FAQCategory({ title, children }) {
  return (
    <div className="mb-12 text-center">
      <h2 className="text-3xl font-bold text-white mb-6">{title}</h2>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  );
}

function FaqItem({ question, answer, showButton, isEmail }) {
  const [isOpen, setIsOpen] = React.useState(false);
  const GOOGLE_FORM_URL = "https://docs.google.com/forms/d/e/1FAIpQLScVJ5NP38k3xJuhVYWnZSFm8PKrzA6Dqu7U73X9fdCI5A2PtQ/viewform?usp=header";

  return (
    <div className="border border-white/10 rounded-lg">
      <button
        className="w-full px-6 py-4 text-center flex justify-between items-center text-white hover:text-[#00ffff]"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="font-semibold flex-grow">{question}</span>
        <ChevronDown className={`w-5 h-5 transition-transform flex-shrink-0 ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      {isOpen && (
        <div className="px-6 py-4 border-t border-white/10 text-center">
          {typeof answer === 'string' ? (
            <p className="text-white/80">{answer}</p>
          ) : (
            <div className="text-white/80">{answer}</div>
          )}
          {showButton && (
            <div className="mt-4 flex justify-center">
              <a
                href={GOOGLE_FORM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-6 py-3 bg-[#00ffff] text-white rounded-lg hover:bg-[#ff0000] transition-colors font-semibold"
              >
                Book a Consultation
              </a>
            </div>
          )}
          {isEmail && (
            <div className="mt-4 flex justify-center">
              <a
                href="mailto:jz@thinkatlasai.com"
                className="inline-block px-6 py-3 bg-[#00ffff] text-white rounded-lg hover:bg-[#ff0000] transition-colors font-semibold"
              >
                Contact Us
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FAQs() {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-4 py-16 text-center">
        <h1 className="text-5xl font-bold text-white mb-6">Frequently Asked Questions</h1>
        <p className="text-xl text-white/80 max-w-3xl mx-auto mb-16">
          Find answers to common questions about Atlas AI's services and solutions.
        </p>
      </div>

      {/* FAQs Grid */}
      <div className="max-w-3xl mx-auto px-4 pb-24">
        <FAQCategory title="General Questions">
          <FaqItem
            question="What services does your agency offer?"
            answer={
              <div className="space-y-2">
                <p>Our agency specializes in:</p>
                <ul className="list-none space-y-2">
                  <li>Lead Generation: Leveraging AI to identify and capture high-quality leads for your business.</li>
                  <li>Website Creation: Designing and developing AI-powered, responsive, and user-friendly websites.</li>
                  <li>Custom Solutions: Tailored AI-driven tools and systems to meet your specific business needs.</li>
                  <li>Customer Retention: Using AI analytics and strategies to retain and engage your existing customers.</li>
                </ul>
              </div>
            }
          />
          <FaqItem
            question="How does AI improve lead generation?"
            answer="AI improves lead generation by analyzing customer data, identifying patterns, and targeting the right audience with personalized campaigns. This results in higher conversion rates and improved ROI."
          />
          <FaqItem
            question="Can you create a custom solution for my business?"
            answer="Absolutely! We work closely with you to understand your goals and design AI-driven solutions tailored to your specific requirements."
          />
        </FAQCategory>

        <FAQCategory title="Lead Generation">
          <FaqItem
            question="How do you source leads?"
            answer="We use advanced AI algorithms to analyze market data, identify potential prospects, and qualify leads based on criteria like industry, demographics, and online behavior."
          />
          <FaqItem
            question="What industries do you specialize in for lead generation?"
            answer="Our AI tools are versatile and work across various industries, including real estate, healthcare, e-commerce, finance, and more."
          />
        </FAQCategory>

        <FAQCategory title="Website Creation">
          <FaqItem
            question="What kind of websites can you create?"
            answer="We build AI-enhanced websites that are responsive, fast, and user-friendly. They include features like chatbots, dynamic content personalization, and seamless integrations with third-party tools."
          />
          <FaqItem
            question="Can you redesign my existing website?"
            answer="Yes, we can revamp your current website to improve its functionality, aesthetics, and performance using AI-driven enhancements."
          />
        </FAQCategory>

        <FAQCategory title="Custom Solutions">
          <FaqItem
            question="What types of custom AI solutions do you offer?"
            answer={
              <div className="space-y-2">
                <p>Our custom solutions include:</p>
                <ul className="list-none">
                  <li>Automated workflows</li>
                  <li>Predictive analytics</li>
                  <li>Natural Language Processing (NLP) tools</li>
                  <li>Custom dashboards for data insights</li>
                </ul>
              </div>
            }
          />
          <FaqItem
            question="How long does it take to create a custom solution?"
            answer="The timeline varies based on the complexity and scope of the project. We'll provide an estimated timeline during our initial consultation."
          />
        </FAQCategory>

        <FAQCategory title="Customer Retention">
          <FaqItem
            question="How can AI help improve customer retention?"
            answer={
              <div className="space-y-2">
                <p>AI helps by:</p>
                <ul className="list-none">
                  <li>Analyzing customer behavior to predict churn</li>
                  <li>Sending personalized recommendations</li>
                  <li>Automating engagement campaigns</li>
                  <li>Enhancing customer support with AI chatbots</li>
                </ul>
              </div>
            }
          />
          <FaqItem
            question="Do you offer ongoing support for retention strategies?"
            answer="Yes, we provide ongoing monitoring, analytics, and optimization to ensure the strategies remain effective."
          />
        </FAQCategory>

        <FAQCategory title="Pricing and Process">
          <FaqItem
            question="How much do your services cost?"
            answer="Our pricing depends on the scope and complexity of your project. Contact us for a custom quote."
          />
          <FaqItem
            question="What is your typical project process?"
            answer={
              <div className="space-y-2">
                <ul className="list-none">
                  <li>Initial consultation to understand your needs</li>
                  <li>Proposal with a tailored strategy</li>
                  <li>Design and development</li>
                  <li>Testing and deployment</li>
                  <li>Ongoing support and optimization</li>
                </ul>
              </div>
            }
          />
          <FaqItem
            question="Do you offer consultations?"
            answer="Yes, we provide free initial consultations to discuss your needs and explore how we can help your business grow."
            showButton={true}
          />
        </FAQCategory>

        <FAQCategory title="Contact Us">
          <FaqItem
            question="How can I get started?"
            answer="You can reach out to us via email, phone, or our website's contact form. We look forward to helping you achieve your goals!"
            isEmail={true}
          />
        </FAQCategory>
      </div>
    </div>
  );
}

export default FAQs;